import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AvatarItem } from '@ui/app-components';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/primitives';
import { formatDateWithIntl, getGMTInfo, getItemPath } from '@ui/utils';
import { TimeMap } from '../time-map';
/**
 * Extracts author info from a potentially partial author object. Returns undefined if author data is missing or has no
 * meaningful name.
 */ export function getAuthorInfo(author) {
    var _ref;
    var _author_name;
    const trimmedName = author === null || author === void 0 ? void 0 : (_author_name = author.name) === null || _author_name === void 0 ? void 0 : _author_name.trim();
    if (!trimmedName) {
        return undefined;
    }
    return {
        name: trimmedName,
        username: author === null || author === void 0 ? void 0 : author.username,
        portrait: (_ref = author === null || author === void 0 ? void 0 : author.portrait) !== null && _ref !== void 0 ? _ref : '',
        id: author === null || author === void 0 ? void 0 : author.id,
        avatarColor: author === null || author === void 0 ? void 0 : author.avatarColor
    };
}
export function TimeStampWithTooltip(param) {
    let { date, currentUserName, author, isCi, placement = 'left', organization } = param;
    if (!date) return null;
    const truncatedUsername = (author === null || author === void 0 ? void 0 : author.name) && (author === null || author === void 0 ? void 0 : author.name.length) > 25 ? "".concat(author === null || author === void 0 ? void 0 : author.name.slice(0, 25), "...") : author === null || author === void 0 ? void 0 : author.name;
    const dateString = (isGMT)=>{
        if (!date) {
            return '';
        }
        const timeZone = isGMT ? undefined : 'UTC';
        const formatDate = formatDateWithIntl(date, timeZone);
        return "".concat(formatDate.month, " ").concat(formatDate.day, ", ").concat(formatDate.year);
    };
    const isCiBadge = ()=>{
        if (typeof isCi !== 'boolean') {
            return '';
        }
        return isCi ? 'on CI' : 'on local';
    };
    return /*#__PURE__*/ _jsx(TooltipProvider, {
        children: /*#__PURE__*/ _jsxs(Tooltip, {
            children: [
                /*#__PURE__*/ _jsx(TooltipTrigger, {
                    asChild: true,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-row items-center gap-2",
                        children: [
                            author && /*#__PURE__*/ _jsx(AvatarItem, {
                                avatar: {
                                    name: author.name,
                                    imageSrc: author.portrait,
                                    href: getItemPath({
                                        authUserName: currentUserName,
                                        organization: organization,
                                        username: author.username,
                                        team: author.username ? undefined : author.id,
                                        type: author.username ? 'user' : 'team'
                                    }),
                                    avatarColor: author.avatarColor
                                },
                                disableTooltip: true
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col gap-0.5 text-sm",
                                children: [
                                    author && /*#__PURE__*/ _jsxs("span", {
                                        className: "text-left text-normal",
                                        children: [
                                            truncatedUsername,
                                            " "
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-left text-xs text-muted-foreground",
                                        children: /*#__PURE__*/ _jsx(TimeMap, {
                                            date: date
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(TooltipContent, {
                    side: placement,
                    className: "max-w-md p-4 border border-border/80",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(TimeMap, {
                                        date: date,
                                        type: "minutes"
                                    }),
                                    !!isCiBadge() && /*#__PURE__*/ _jsx("span", {
                                        className: "rounded-full bg-muted px-2 py-0.5 text-xs",
                                        children: isCiBadge()
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "rounded bg-muted px-2 py-1 text-xs font-medium",
                                        children: "UTC"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-1 items-center justify-between gap-4 text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-sm",
                                                children: dateString()
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-sm",
                                                children: formatDateWithIntl(date, 'UTC').time
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "rounded bg-muted px-2 py-1 text-xs font-medium",
                                        children: [
                                            "GMT ",
                                            getGMTInfo()
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-1 items-center justify-between gap-4 text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-sm",
                                                children: dateString(true)
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-sm",
                                                children: formatDateWithIntl(date).time
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                })
            ]
        })
    });
}
